import { detect } from 'tinyld';
import { getAllLanguageCodes } from '@/shared/constants/languageConstants';

/** Share of sampled rows a language must exceed to be pre-selected. */
export const LANGUAGE_SHARE_THRESHOLD = 0.03;

/**
 * How many example-column cells to scan for language detection — same upper
 * bound as csvPreviewUtils' first-N walk so we stay on the iterated preview.
 */
export const LANGUAGE_DETECTION_SAMPLE_SIZE = 25;

const SUPPORTED_CODES = new Set(getAllLanguageCodes());

/**
 * Detect the ISO 639-1 code for one text sample, restricted to languages the
 * task language selector supports. Returns null when undetermined / unsupported.
 */
export function detectLanguageCode(text) {
    const sample = typeof text === 'string' ? text.trim() : '';
    if (!sample) return null;
    try {
        const code = detect(sample, { only: [...SUPPORTED_CODES] });
        if (!code || !SUPPORTED_CODES.has(code)) return null;
        return code;
    } catch {
        return null;
    }
}

/**
 * Count languages across sampled texts and keep those whose share is strictly
 * above `threshold` (default 3%). Order is most-frequent first.
 *
 * @param {string[]} texts
 * @param {number} [threshold]
 * @returns {string[]} ISO 639-1 codes
 */
export function significantLanguagesFromTexts(texts, threshold = LANGUAGE_SHARE_THRESHOLD) {
    const samples = (texts || [])
        .map((value) => (typeof value === 'string' ? value : value == null ? '' : String(value)))
        .map((value) => value.trim())
        .filter(Boolean);
    if (!samples.length) return [];

    const counts = new Map();
    for (const sample of samples) {
        const code = detectLanguageCode(sample);
        if (!code) continue;
        counts.set(code, (counts.get(code) || 0) + 1);
    }
    if (!counts.size) return [];

    const total = samples.length;
    return [...counts.entries()]
        .filter(([, count]) => count / total > threshold)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([code]) => code);
}
